import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { errorToast, successToast } from '../../../Toast';
import { DISABLED_USER_TEXT, ENABLED_USER_TEXT } from '../data/constants';
import apiClient from './apiClient';
import { Role } from './roles';

export const USERS_KEY = 'users';

// Types
export interface EditUserPayload {
	username: string;
	name?: string;
	email?: string;
}

export interface EditUserPasswordPayload {
	password: string;
}

export interface AccountResponse {
	accountType: string;
	createdTimestamp: string;
	deleted: boolean;
	userLocator: string;
	locked: boolean;
	name: string;
	tenant: string;
	username: string;
	roles: Role[];
	email?: string;
}

export interface AccountRoleUpdateRequest {
	assignRoles?: string[];
	unassignRoles?: string[];
	replaceAll?: boolean;
}

export interface AddUserPayload {
	username: string;
	password: string;
	type: string; //account.tenant.employee || account.tenant.read.only.user || account.tenant.claims.only.user;
	name: string;
	roles: string[];
	email?: string;
}

export interface EditUserPayload {
	username: string;
	name?: string;
	email?: string;
}

export interface EditUserStatusPayload {
	status: string;
}

// Axios Requests
const getUsers = async (authToken: string) => {
	const res = await apiClient.get<{ resultsPage: AccountResponse[] }>(
		'/account/v1/findByTenant?limit=2000',
		{
			headers: {
				Authorization: authToken,
			},
		},
	);

	return res.data.resultsPage;
};

const getUsersSSO = async (tenantHostname: string) => {
	const res = await apiClient.get<{ resultsPage: AccountResponse[] }>(
		'/account/v1/findByTenant?limit=2000&ns_tenant_host=' + tenantHostname,
	);

	return res.data.resultsPage;
};

const addUser = async (request: AddUserPayload, authToken: string) => {
	const res = await apiClient.post<AccountResponse>(
		'/account/v1/account',
		request,
		{
			headers: {
				Authorization: authToken,
			},
		},
	);

	return res.data;
};

const addUserSSO = async (request: AddUserPayload, tenantHostname: string) => {
	const res = await apiClient.post<AccountResponse>(
		'/account/v1/account?ns_tenant_host=' + tenantHostname,
		request,
	);

	return res.data;
};

const editUser = async (
	authToken: string,
	userID: string,
	request: EditUserPayload,
) => {
	await apiClient.patch<AccountResponse>(
		`/account/v1/account/${userID}`,
		request,
		{
			headers: {
				Authorization: authToken,
			},
		},
	);
};

const editUserRoles = async (
	authToken: string,
	userID: string,
	request: AccountRoleUpdateRequest,
) => {
	await apiClient.patch(`/account/${userID}/roles`, request, {
		headers: {
			Authorization: authToken,
		},
	});
};

const editUserRolesSSO = async (
	tenantHostname: string,
	userID: string,
	request: AccountRoleUpdateRequest,
) => {
	await apiClient.patch(
		`/account/${userID}/roles?ns_tenant_host=${tenantHostname}`,
		request,
	);
};

const editPassword = async (
	authToken: string,
	userID: string,
	password: string,
) => {
	if (password) {
		await apiClient.put(
			`/account/v1/account/${userID}/password`,
			{ password },
			{
				headers: {
					Authorization: authToken,
				},
			},
		);
	}
};

const toggleUserStatus = async (
	authToken: string,
	userID: string,
	status: boolean, //account.status.enabled || account.status.deleted
) => {
	const statusString = status
		? 'account.status.deleted'
		: 'account.status.enabled';
	await apiClient.put(
		`/account/v1/account/${userID}/status`,
		{ status: statusString },
		{
			headers: {
				Authorization: authToken,
			},
		},
	);
};

const toggleUserStatusSSO = async (
	tenantHostname: string,
	userID: string,
	status: boolean, //account.status.enabled || account.status.deleted
) => {
	const statusString = status
		? 'account.status.deleted'
		: 'account.status.enabled';
	await apiClient.put(
		`/account/v1/account/${userID}/status?ns_tenant_host=${tenantHostname}`,
		{ status: statusString },
	);
};

// Queries
const useUsers = (
	queryKey: string[],
	tenantHostname: string,
	token: string,
) => {
	return useQuery({
		queryKey,
		queryFn: () =>
			tenantHostname ? getUsersSSO(tenantHostname) : getUsers(token),
	});
};

// Mutations
const useAddUserMutation = (
	queryKey: string[],
	tenantHostname: string,
	token: string,
	successCallback?: () => void,
) => {
	const client = useQueryClient();
	return useMutation(
		async (newUser: AddUserPayload) => {
			const createdUser = tenantHostname
				? await addUserSSO(newUser, tenantHostname)
				: await UsersApi.addUser(newUser, token);
			tenantHostname
				? await editUserRolesSSO(tenantHostname, createdUser.userLocator, {
						assignRoles: newUser.roles,
						replaceAll: true,
				  })
				: await UsersApi.editUserRoles(token, createdUser.userLocator, {
						assignRoles: newUser.roles,
						replaceAll: true,
				  });
			return createdUser;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(queryKey);
				successToast('Successfully added user');
				successCallback && successCallback();
			},
			onError: (err) => {
				if (axios.isAxiosError(err) && err.response.data.message) {
					errorToast('Error adding user: ' + err.response.data.message);
				} else {
					errorToast('Error adding user');
				}
			},
			onSettled: () => {
				client.invalidateQueries(queryKey);
			},
		},
	);
};

const useUpdateUserMutation = (
	queryKey: string[],
	tenantHostname: string,
	token: string,
	userId: string,
	successCallback?: () => void,
) => {
	const client = useQueryClient();
	return useMutation(
		(request: AddUserPayload) => {
			const promises = [];
			if (!tenantHostname) {
				promises.push(editUser(token, userId, request));
				if (request.password) {
					promises.push(editPassword(token, userId, request.password));
				}
			}
			if (request.roles) {
				promises.push(
					tenantHostname
						? editUserRolesSSO(tenantHostname, userId, {
								assignRoles: request.roles,
								replaceAll: true,
						  })
						: editUserRoles(token, userId, {
								assignRoles: request.roles,
								replaceAll: true,
						  }),
				);
			}
			return Promise.all(promises);
		},
		{
			onSuccess: () => {
				client.invalidateQueries(queryKey);
				successToast('Successfully updated user');
				successCallback && successCallback();
			},
			onError: (err) => {
				if (axios.isAxiosError(err) && err.response.data.message) {
					errorToast('Error updating user: ' + err.response.data.message);
				} else {
					errorToast('Error updating user');
				}
			},
			onSettled: () => {
				client.invalidateQueries(queryKey);
			},
		},
	);
};

const useToggleUserStatusMutation = (
	queryKey: string[],
	tenantHostname: string,
	token: string,
	user: AccountResponse,
	successCallback?: () => void,
) => {
	const client = useQueryClient();
	return useMutation(
		(user: AccountResponse) => {
			const statusBool = !user.deleted;
			return tenantHostname
				? toggleUserStatusSSO(tenantHostname, user.userLocator, statusBool)
				: UsersApi.toggleUserStatus(token, user.userLocator, statusBool);
		},
		{
			onSuccess: () => {
				client.invalidateQueries(queryKey);
				const toggled = user.deleted
					? ENABLED_USER_TEXT()
					: DISABLED_USER_TEXT();
				successToast(`Successfully ${toggled.toLowerCase()} ${user.name}`);
				successCallback && successCallback();
			},
			onError: (err) => {
				if (axios.isAxiosError(err) && err.response.data.message) {
					errorToast('Error updating user: ' + err.response.data.message);
				} else {
					errorToast('Error updating user');
				}
			},
			onSettled: () => {
				client.invalidateQueries(queryKey);
			},
		},
	);
};

const UsersApi = {
	getUsers,
	addUser,
	editUser,
	editUserRoles,
	editPassword,
	toggleUserStatus,
	useUsers,
	useAddUserMutation,
	useUpdateUserMutation,
	useToggleUserStatusMutation,
	USERS_KEY,
};

export default UsersApi;
